import React, { useState, useEffect } from "react";
import { SidebarData } from "../../json/siderbar_data";
import { useToast } from "../../../Toaster/ToastContext";
import { StoreRolesPermissions } from "../../../services/authService";
import PropTypes from "prop-types";

const AddRole = ({ onSave, roles }) => {
  const [role, setRole] = useState("");
  const { showToast } = useToast();
  const [errors, setErrors] = useState({
    role: "",
  });

  const validateField = (name, value) => {
    switch (name) {
      case "role":
        return value.trim() ? "" : "Role is required";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {
      role: validateField("role", role),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const initializePermissions = (items, existingPermissions = [], userType) => {
    return items.map((item) => {
      const hasSubmenu = item.submenuItems && item.submenuItems.length > 0;
      const existing =
        existingPermissions.find((p) => p.title === item.label) || {};
      let childPage = hasSubmenu
        ? initializePermissions(
            item.submenuItems,
            existing.childPage || [],
            userType
          )
        : [];

      // Filter out "Owner Dashboard" if userType is "business"
      if (userType === "business") {
        childPage = childPage.filter(
          (child) => child.title !== "Owner Dashboard"
        );
      }

      return {
        title: item.label,
        enabled: existing.enabled || false,
        submenu: hasSubmenu,
        url: item.link || "",
        childPage: childPage,
        create:
          !hasSubmenu && item.label !== "Dashboard"
            ? existing.create || false
            : undefined,
        read:
          !hasSubmenu && item.label !== "Dashboard"
            ? existing.read || false
            : undefined,
        update:
          !hasSubmenu && item.label !== "Dashboard"
            ? existing.update || false
            : undefined,
        delete:
          !hasSubmenu && item.label !== "Dashboard"
            ? existing.delete || false
            : undefined,
        pdf: !hasSubmenu && item.label.includes("Report") ? existing.pdf || false : undefined,
        excel: !hasSubmenu && item.label.includes("Report") ? existing.excel || false : undefined,
      };
    });
  };

  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const userType = localStorage.getItem("user_type")?.toLowerCase();
    // Filter out "User Management" section if userType is "business"
    const filteredSidebarData =
      userType === "business"
        ? SidebarData.filter((section) => section.label !== "User Management")
        : SidebarData;

    if (roles) {
      setRole(roles.roles || "");
      const parsedPermissions = JSON.parse(roles.permissions);
      setPermissions(
        filteredSidebarData.map((section) => {
          const existingSection =
            parsedPermissions.permissions.find(
              (p) => p.title === section.label
            ) || {};

          return {
            title: section.label,
            actions: [],
            parentEnabled: existingSection.enabled || false,
            childPage: section.submenuItems
              ? initializePermissions(
                  section.submenuItems,
                  existingSection.childPage || [],
                  userType
                )
              : [],
          };
        })
      );
    } else {
      setRole("");
      setPermissions(
        filteredSidebarData.map((section) => ({
          title: section.label,
          actions: [],
          parentEnabled: false,
          childPage: section.submenuItems
            ? initializePermissions(section.submenuItems, [], userType)
            : [],
        }))
      );
    }
  }, [roles]);

  const handleSectionToggle = (sectionIndex) => {
    const newPermissions = [...permissions];
    const section = newPermissions[sectionIndex];
    section.parentEnabled = !section.parentEnabled;

    section.childPage.forEach((item) => {
      item.enabled = section.parentEnabled;
      if (item.childPage && item.childPage.length > 0) {
        item.childPage.forEach((subItem) => {
          subItem.enabled = section.parentEnabled;
          if (subItem.title !== "Dashboard" && !subItem.submenu && section.title !== "Dashboard") {
            subItem.create = section.parentEnabled;
            subItem.read = section.parentEnabled;
            subItem.update = section.parentEnabled;
            subItem.delete = section.parentEnabled;
          }
        });
      }
      if (item.title !== "Dashboard" && !item.submenu && section.title !== "Dashboard") {
        if (section.title === "Reports") {
          item.pdf = section.parentEnabled;
          item.excel = section.parentEnabled;
        } else {
          item.create = section.parentEnabled;
          item.read = section.parentEnabled;
          item.update = section.parentEnabled;
          item.delete = section.parentEnabled;
        }
      }
    });

    setPermissions(newPermissions);
  };

  const handlePermissionChange = (sectionIndex, itemIndex, action) => {
    const newPermissions = [...permissions];
    const section = newPermissions[sectionIndex];
    const item = section.childPage[itemIndex];

    if (action === "enabled") {
      item.enabled = !item.enabled;

      if (item.childPage) {
        item.childPage.forEach((subItem) => {
          subItem.enabled = item.enabled;

          if (section.title === "Reports") {
            subItem.pdf = item.enabled;
            subItem.excel = item.enabled;
          } else if (subItem.title !== "Dashboard" && !subItem.submenu) {
            subItem.create = item.enabled;
            subItem.read = item.enabled;
            subItem.update = item.enabled;
            subItem.delete = item.enabled;
          }
        });
      }

      if (item.enabled && !item.submenu && item.title !== "Dashboard") {
        if (section.title === "Reports") {
          item.pdf = true;
          item.excel = true;
        } else {
          item.create = true;
          item.read = true;
          item.update = true;
          item.delete = true;
        }
      } else if (!item.enabled && !item.submenu && item.title !== "Dashboard") {
        if (section.title === "Reports") {
          item.pdf = false;
          item.excel = false;
        } else {
          item.create = false;
          item.read = false;
          item.update = false;
          item.delete = false;
        }
      }

      section.parentEnabled = section.childPage.some((child) => child.enabled);
    } else if (!item.submenu && item.title !== "Dashboard") {
      if (section.title === "Reports") {
        item[action] = !item[action]; // toggle pdf/excel
      } else {
        item[action] = !item[action]; // toggle CRUD
      }
    }

    setPermissions(newPermissions);
  };

  const handleSubItemToggle = (sectionIndex, itemIndex, subIndex) => {
    const newPermissions = [...permissions];
    const section = newPermissions[sectionIndex];
    const item = section.childPage[itemIndex];
    const subItem = item.childPage[subIndex];

    subItem.enabled = !subItem.enabled;
    if (
      subItem.title !== "Dashboard" &&
      !subItem.submenu &&
      item.title !== "Dashboard"
    ) {
      subItem.create = subItem.enabled;
      subItem.read = subItem.enabled;
      subItem.update = subItem.enabled;
      subItem.delete = subItem.enabled;
    }
    item.enabled = item.childPage.some((sub) => sub.enabled);
    section.parentEnabled = section.childPage.some((child) => child.enabled);
    setPermissions(newPermissions);
  };

  const handleSubPermissionChange = (
    sectionIndex,
    itemIndex,
    subIndex,
    action
  ) => {
    const newPermissions = [...permissions];
    const section = newPermissions[sectionIndex];
    const item = section.childPage[itemIndex];
    const subItem = item.childPage[subIndex];

    if (
      !subItem.submenu &&
      subItem.title !== "Dashboard" &&
      item.title !== "Dashboard"
    ) {
      subItem[action] = !subItem[action];
    }
    setPermissions(newPermissions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const formatPermissions = (
          items,
          isDashboardSubtree = false,
          parentTitle = ""
        ) => {
          return items.map((item) => {
            const isInDashboardSubtree =
              isDashboardSubtree ||
              item.title === "Dashboard" ||
              (item.submenu &&
                item.childPage?.some((child) => child.title === "Dashboard"));

            const permissionObj = {
              title: item.title,
              enabled: item.enabled,
              submenu: item.submenu,
              url: item.url || "",
            };

            if (parentTitle === "Reports" && !item.submenu) {
              permissionObj.pdf = item.pdf || false;
              permissionObj.excel = item.excel || false;
            } else if (
              !isInDashboardSubtree &&
              !item.submenu &&
              item.title !== "Dashboard"
            ) {
              permissionObj.create = item.create;
              permissionObj.read = item.read;
              permissionObj.update = item.update;
              permissionObj.delete = item.delete;
            }

            if (item.childPage && item.childPage.length > 0) {
              permissionObj.childPage = formatPermissions(
                item.childPage,
                isInDashboardSubtree || item.title === "Dashboard",
                parentTitle || item.title
              );
            }

            return permissionObj;
          });
        };

        const formattedData = {
          id: roles ? roles.id : "",
          role: role,
          permissions: permissions.map((section) => ({
            title: section.title,
            enabled: section.parentEnabled,
            url: section.url,
            childPage: formatPermissions(
              section.childPage,
              section.title === "Dashboard",
              section.title
            ),
          })),
        };

        const roleData = {
          id: roles ? roles.id : "",
          name: role,
          permissions: formattedData.permissions,
        };

        const response = await StoreRolesPermissions(roleData);
        if (response.ok) {
          onSave && onSave();
          setRole("");
          setErrors({ role: "" });
          setPermissions(
            SidebarData.map((section) => ({
              title: section.label,
              url: section.submenuItems?.[0]?.link || "",
              actions: [],
              parentEnabled: false,
              childPage: section.submenuItems
                ? initializePermissions(section.submenuItems)
                : [],
            }))
          );
          document.querySelector('[data-bs-dismiss="modal"]').click();
          showToast("Role created successfully", "success");
        }
      } catch (error) {
        showToast(error.message || "Failed to create role", "error");
      }
    }
  };

  return (
    <div>
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                  <div className="page-title">
                    <h4>Create Role</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label className="form-label">Role Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.role ? "is-invalid" : ""
                        }`}
                        value={role}
                        onChange={(e) => {
                          setRole(e.target.value);
                          setErrors((prev) => ({
                            ...prev,
                            role: validateField("role", e.target.value),
                          }));
                        }}
                        required
                      />
                      {errors.role && (
                        <div className="invalid-feedback">{errors.role}</div>
                      )}
                    </div>
                    <div className="permissions-section">
                      <h5 className="mb-3">Permissions</h5>
                      {permissions.map((section, sectionIndex) => (
                        <div
                          key={section.title}
                          className="permission-group mb-4"
                        >
                          <div className="d-flex align-items-center mb-3">
                            <input
                              type="checkbox"
                              id={`section-${section.title}-enable`}
                              checked={section.parentEnabled}
                              onChange={() => handleSectionToggle(sectionIndex)}
                              className="form-check-input me-2"
                            />
                            <h6 className="permission-group-title mb-0">
                              {section.title}
                            </h6>
                          </div>
                          <div className="row">
                            {section.childPage.map((item, itemIndex) => (
                              <div key={itemIndex} className="col-md-4 mb-3">
                                <div className="permission-item">
                                  <div className="d-flex align-items-center">
                                    <input
                                      type="checkbox"
                                      id={`${section.title}-${item.title}-enable`}
                                      checked={item.enabled}
                                      onChange={() =>
                                        handlePermissionChange(
                                          sectionIndex,
                                          itemIndex,
                                          "enabled"
                                        )
                                      }
                                      className="form-check-input me-2"
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`${section.title}-${item.title}-enable`}
                                    >
                                      {item.title}
                                    </label>
                                  </div>
                                  {item.enabled &&
                                    item.title !== "Dashboard" &&
                                    !item.submenu &&
                                    section.title !== "Dashboard" && (
                                      <div className="ms-4 mt-2">
                                        <div className="d-flex flex-column gap-2">
                                          {(() => {
                                            let allowedActions = [
                                              "create",
                                              "read",
                                              "update",
                                              "delete",
                                            ];
                                            if (
                                              item.title === "Expired Products"
                                            ) {
                                              allowedActions = ["delete"];
                                            } else if (
                                              item.title === "Low Stocks"
                                            ) {
                                              allowedActions = ["delete"];
                                            } else if (
                                              item.title === "Stock Transfer"
                                            ) {
                                              allowedActions = ["create"];
                                            } else if (
                                              item.title === "Purchase Order" ||
                                              item.title === "Purchase Return"
                                            ) {
                                              allowedActions = []; // hide all actions
                                            } else if (
                                              item.title.includes("Report")
                                            ) {
                                              allowedActions = ["pdf", "excel"];
                                            }
                                            return allowedActions.map(
                                              (action) => (
                                                <div
                                                  key={action}
                                                  className="d-flex align-items-center"
                                                >
                                                  <input
                                                    type="checkbox"
                                                    id={`${section.title}-${item.title}-${action}`}
                                                    checked={
                                                      item[action] || false
                                                    }
                                                    onChange={() =>
                                                      handlePermissionChange(
                                                        sectionIndex,
                                                        itemIndex,
                                                        action
                                                      )
                                                    }
                                                    className="form-check-input me-2"
                                                  />
                                                  <label
                                                    className="form-check-label text-capitalize"
                                                    htmlFor={`${section.title}-${item.title}-${action}`}
                                                  >
                                                    {action.replace("_", " ")}
                                                  </label>
                                                </div>
                                              )
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    )}
                                  {item.enabled &&
                                    item.submenu &&
                                    item.childPage &&
                                    item.childPage.length > 0 && (
                                      <div className="ms-4 mt-2">
                                        {item.childPage.map(
                                          (subItem, subIndex) => (
                                            <div
                                              key={subIndex}
                                              className="permission-sub-item"
                                            >
                                              <div className="d-flex align-items-center">
                                                <input
                                                  type="checkbox"
                                                  id={`${section.title}-${item.title}-${subItem.title}-enable`}
                                                  checked={subItem.enabled}
                                                  onChange={() =>
                                                    handleSubItemToggle(
                                                      sectionIndex,
                                                      itemIndex,
                                                      subIndex
                                                    )
                                                  }
                                                  className="form-check-input me-2"
                                                />
                                                <label
                                                  className="form-check-label"
                                                  htmlFor={`${section.title}-${item.title}-${subItem.title}-enable`}
                                                >
                                                  {subItem.title}
                                                </label>
                                              </div>
                                              {subItem.enabled &&
                                                subItem.title !== "Dashboard" &&
                                                !subItem.submenu &&
                                                item.title !== "Dashboard" && (
                                                  <div className="ms-4 mt-2">
                                                    <div className="d-flex flex-column gap-2">
                                                      {(() => {
                                                        let allowedActions = [
                                                          "create",
                                                          "read",
                                                          "update",
                                                          "delete",
                                                        ];
                                                        if (
                                                          subItem.title ===
                                                          "Expired Products"
                                                        ) {
                                                          allowedActions = [
                                                            "delete",
                                                          ];
                                                        } else if (
                                                          subItem.title ===
                                                          "Low Stocks"
                                                        ) {
                                                          allowedActions = [
                                                            "delete",
                                                          ];
                                                        } else if (
                                                          subItem.title ===
                                                          "Stock Transfer"
                                                        ) {
                                                          allowedActions = [
                                                            "create",
                                                          ];
                                                        } else if (
                                                          subItem.title ===
                                                          "Sales"
                                                        ) {
                                                          allowedActions = [
                                                            "create",
                                                            "delete",
                                                          ];
                                                        }
                                                        return allowedActions.map(
                                                          (action) => (
                                                            <div
                                                              key={action}
                                                              className="d-flex align-items-center"
                                                            >
                                                              <input
                                                                type="checkbox"
                                                                id={`${section.title}-${item.title}-${subItem.title}-${action}`}
                                                                checked={
                                                                  subItem[
                                                                    action
                                                                  ] || false
                                                                }
                                                                onChange={() =>
                                                                  handleSubPermissionChange(
                                                                    sectionIndex,
                                                                    itemIndex,
                                                                    subIndex,
                                                                    action
                                                                  )
                                                                }
                                                                className="form-check-input me-2"
                                                              />
                                                              <label
                                                                className="form-check-label text-capitalize"
                                                                htmlFor={`${section.title}-${item.title}-${subItem.title}-${action}`}
                                                              >
                                                                {action}
                                                              </label>
                                                            </div>
                                                          )
                                                        );
                                                      })()}
                                                    </div>
                                                  </div>
                                                )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-submit">
                        Create Role
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AddRole.propTypes = {
  roles: PropTypes.shape({
    id: PropTypes.string,
    roles: PropTypes.string,
    permissions: PropTypes.string,
  }),
  onSave: PropTypes.func,
};

AddRole.defaultProps = {
  roles: null,
};

export default AddRole;
